// src/config/serviceConfig.ts
import AppliedInternshipDSP from "@/pages/services/dsp/AppliedInternshipDSP";
import IaaSDSP from "@/pages/services/dsp/IaaSDSP";
import SelfEvaluationDSP from "@/pages/services/dsp/SelfEvaluationDSP";
import JobReadinessAssistanceDSP from "@/pages/services/dsp/JobReadinessAssistanceDSP";
import {
  HandRaisedIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/outline";
import type { ComponentType, ReactNode } from "react";
import FixedPriceProjectDSP from "@/pages/services/dsp/FixedPriceProjectDSP";
import WorkshopDSP from "@/pages/services/dsp/WorkshopDSP";

export enum ServiceGroups {
  ACADEMIC = "Academic Programs",
  CAREER = "Career Development Programs",
  CORPORATE = "Corporate Services",
}

export type IconConfig = {
  bgClass: string;
  Icon: ComponentType<React.SVGProps<SVGSVGElement>>;
};

export interface ServiceMeta {
  id: number;
  badge: string;
  displayName: string;   // ✅ human-friendly name
  icon: IconConfig;      // ✅ icon styling
  description?: ReactNode; // ✅ optional JSX description
  serviceFormId?: number; // ✅ optional form mapping
  serviceDetails?:any;
  requestRoute?: string;
}

export type ServiceShortNames =
  | "SELF_EVALUATION"
  | "JRA"
  | "APPLIED_INTERNSHIP"
  | "FIXED_PRICE_PROJECT"
  | "INTERVIEW_SERVICE"
;

// ✅ Central mapping: keys = DB shortnames
export const serviceMeta: Record<string, ServiceMeta> = {
  SELF_EVALUATION: {
    id: 1,
    badge:"SE",
    displayName: "Self Evaluation",
    icon: { bgClass: "bg-blue-50 text-blue-600", Icon: HandRaisedIcon },
    serviceFormId: 3,
    serviceDetails: SelfEvaluationDSP,
    requestRoute: "/self-evaluation",
  },
  JRA: {
    id: 2,
    badge:"JRA",
    displayName: "Job Rediness Assistance",
    icon: { bgClass: "bg-blue-50 text-blue-600", Icon: HandRaisedIcon },
    serviceFormId: 3,
    serviceDetails: JobReadinessAssistanceDSP,
    requestRoute: "/job-readiness",
  },
  APPLIED_INTERNSHIP: {
    id: 3,
    badge:"AI",
    displayName: "Applied Intership",
    icon: { bgClass: "bg-blue-50 text-blue-600", Icon: HandRaisedIcon },
    serviceFormId: 3,
    serviceDetails: AppliedInternshipDSP,
    requestRoute: "/internship",
  },
  FIXED_PRICE_PROJECT: {
    id: 4,
    badge:"FPP",
    displayName: "Fixed Price Project",
    icon: { bgClass: "bg-blue-50 text-blue-600", Icon: HandRaisedIcon },
    serviceFormId: 3,
    serviceDetails: FixedPriceProjectDSP,
    requestRoute: "/fpp",
  },
  WORKSHOP: {
    id: 5,
    badge:"WS",
    displayName: "Workshop",
    icon: { bgClass: "bg-blue-50 text-blue-600", Icon: HandRaisedIcon },
    serviceFormId: 3,
    serviceDetails: WorkshopDSP,
    requestRoute: "/ws",
  },
  INTERVIEW_SERVICE: {
    id: 6,
    badge:"IaaS",
    displayName: "Interview-as-a-Service",
    icon: { bgClass: "bg-amber-50 text-amber-600", Icon: BriefcaseIcon },
    serviceDetails: IaaSDSP,
    requestRoute: "/iaas",
  },
}

export const academicLayout: string[][] = [
  ["SELF_EVALUATION", "JRA", "APPLIED_INTERNSHIP"],
  ["WORKSHOP", "FIXED_PRICE_PROJECT","WORKSHOP"],
];

export const careerLayout: string[][] = [
  ["SELF_EVALUATION", "JRA", "WORKSHOP"],
];

export const corporateLayout: string[][] = [
  ["INTERVIEW_SERVICE", "WORKSHOP"],
];

export const userTypeLabel: Record<string, string> = {
  "INDIVIDUAL": "Career Development Programs", // Career Development
  "ACADEMIC": "Academic Services", // Academic
  "CORPORATE": "Corporate Services", // Corporate
  "7HILLS": "7Hills Services"
};

// ✅ Map user_type → list of service shortnames
export const serviceApplicability: Record<string, string[]> = {
  "7HILLS": Object.keys(serviceMeta), // All services
  "INDIVIDUAL": ["SELF_EVALUATION", "JRA","WORKSHOP"], 
  "ACADEMIC": ["SELF_EVALUATION", "JRA","APPLIED_INTERNSHIP", "FIXED_PRICE_PROJECT", "WORKSHOP"], 
  "CORPORATE": ["INTERVIEW_SERVICE","WORKSHOP"],
};

export const userTypeToServiceGroup: Record<string, ServiceGroups> = {
  INDIVIDUAL: ServiceGroups.CAREER,
  ACADEMIC: ServiceGroups.ACADEMIC,
  CORPORATE: ServiceGroups.CORPORATE,
};

export const serviceGroupRoutes = {
    [ServiceGroups.CAREER]: "career",
    [ServiceGroups.ACADEMIC]: "academic",
    [ServiceGroups.CORPORATE]: "corporate",
};

export const routeToServiceGroup = {
    career: ServiceGroups.CAREER,
    academic: ServiceGroups.ACADEMIC,
    corporate: ServiceGroups.CORPORATE,
};